import type { JSX, ReactNode } from "react";

import { DEFAULT_IDENTITY_CELLS, type IdentityCell } from "@noko-city/shared";

import { CityClock } from "./CityClock";
import { ClockGlyph } from "./ClockGlyph";
import { MoneyGlyph } from "./MoneyGlyph";
import { PlaneGlyph } from "./PlaneGlyph";
import { WeatherGlyph } from "./WeatherGlyph";

import type { CityPageLive } from "@/lib/api";
import { readArrivals } from "@/lib/arrivals";
import { type CurrentWeatherReading, forecastRangeLabel, readCurrentWeather } from "@/lib/weather";

/** One header cell: the icon that names it, its label, and the reading it states. */
interface Vital {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  hint?: string;
}

/** The header's own condition glyph, or nothing when the payload carries no usable code. */
function conditionGlyph(weather: CurrentWeatherReading | null): ReactNode {
  const glyph = weather?.glyph;
  return glyph ? <WeatherGlyph kind={glyph.kind} night={glyph.night} /> : null;
}

/** What the header needs: the city's readings, its timezone and the cells kept. */
interface GuideHeaderVitalsProps {
  live: CityPageLive | null | undefined;
  timezone: string;
  /** The cells to draw, in order; every cell of the header when omitted. */
  cells?: readonly IdentityCell[];
}

/**
 * The guide header's live readings, one cell each, every cell carrying the icon
 * that names it at a glance: the condition glyph, the arriving aircraft, money
 * and a clock.
 *
 * The cells an author kept are drawn in the order they kept them, and one with no
 * reading draws nothing — a city with no feeds gets a row of one rather than a
 * fabricated figure. The clock is the exception to the reading rule and not to the
 * selection rule: every city has a timezone, so it draws whenever it was kept.
 *
 * WEATHER states the reading the directory row draws — the same whole degrees and
 * the same condition glyph, so one city cannot read `26°` on the home page and
 * something else in its own guide — and keeps the forecast's next-24 h range as
 * the cell's tooltip. A page baked before the header drew the condition carries
 * neither field and falls back to printing that range.
 */
export function GuideHeaderVitals({ live, timezone, cells }: GuideHeaderVitalsProps): JSX.Element {
  const weather = readCurrentWeather(live?.weather);
  const liveWeather = live?.weather;
  const available: Partial<Record<IdentityCell, Vital>> = {};

  if (liveWeather) {
    const range = forecastRangeLabel(liveWeather.minC, liveWeather.maxC);
    available.weather = {
      label: "WEATHER",
      value: weather ? weather.label : range,
      icon: conditionGlyph(weather),
      hint: `Next 24 hours: ${range}`,
    };
  }
  // Seats on today's arrivals, an estimate of people rather than a count of them, so the wording
  // that says `estimated` travels in the cell's tooltip. ARRIVALS is the arrivals board's own
  // word for this figure and the label the flights tool uses for its column, where `PASSENGERS`
  // read as a total. The reading itself — the guard, the number and that wording — is shared with
  // the home directory row, which draws the same figure for the same city.
  const arrivals = readArrivals(live?.flights?.arrivals);
  if (arrivals) {
    available.flights = {
      label: "ARRIVALS",
      icon: <PlaneGlyph />,
      hint: arrivals.hint,
      value: arrivals.label,
    };
  }
  if (live?.currency) {
    available.money = {
      label: `€1 IN ${live.currency.code}`,
      icon: <MoneyGlyph />,
      value: live.currency.ratePerEur.toFixed(2),
    };
  }
  available.clock = {
    label: "LOCAL TIME",
    icon: <ClockGlyph />,
    value: <CityClock timezone={timezone} />,
  };

  const vitals = (cells ?? DEFAULT_IDENTITY_CELLS)
    .map((cell) => available[cell])
    .filter((vital): vital is Vital => vital !== undefined);

  return (
    <ul className="atlas-guide-vitals">
      {vitals.map((vital) => (
        <li key={vital.label} className="atlas-guide-vital" title={vital.hint}>
          <p className="atlas-guide-vital-label">
            {vital.icon}
            {vital.label}
          </p>
          <p className="atlas-guide-vital-value">{vital.value}</p>
        </li>
      ))}
    </ul>
  );
}
